import React, { useState } from 'react';
import { Pentagon, Plus, Eye, Edit2, Trash2, MapPin, Search } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { VesselMap } from '@/components/map/VesselMap';
import { GeofenceModal } from '@/components/geofence/GeofenceModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useApp } from '@/context/AppContext';

export default function GeofencePage() {
  const { 
    geofences, 
    selectGeofence, 
    deleteGeofence,
    setIsDrawingGeofence,
    isDrawingGeofence,
    createGeofence,
    setMapCenter,
    setMapZoom
  } = useApp();

  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [highlightedGeofence, setHighlightedGeofence] = useState(null);
  
  const [newGeofence, setNewGeofence] = useState({
    name: '',
    type: 'port',
    portAreaType: 'Harbor',
    color: '#1E88C7'
  });

  const filteredGeofences = geofences.filter(g => 
    g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.portAreaType.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleView = (geofence) => {
    selectGeofence(geofence);
    setHighlightedGeofence(geofence);
  };

  const handleEdit = (geofence) => {
    // For demo, just highlight on map
    setHighlightedGeofence(geofence);
    const centerLat = geofence.polygonCoords.reduce((sum, c) => sum + c[0], 0) / geofence.polygonCoords.length;
    const centerLon = geofence.polygonCoords.reduce((sum, c) => sum + c[1], 0) / geofence.polygonCoords.length;
    setMapCenter([centerLat, centerLon]);
    setMapZoom(15);
  };

  const handleDelete = (geofence) => {
    setSelectedForDelete(geofence);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedForDelete) {
      deleteGeofence(selectedForDelete.id);
      setDeleteDialogOpen(false);
      setSelectedForDelete(null);
    }
  };

  const handleCreateGeofence = () => {
    // Demo: Create with mock polygon
    const mockPolygon = [
      [-5.115, 119.395],
      [-5.115, 119.410],
      [-5.105, 119.410],
      [-5.105, 119.395],
      [-5.115, 119.395]
    ];
    
    createGeofence({
      ...newGeofence,
      polygonCoords: mockPolygon
    });
    
    setCreateDialogOpen(false);
    setNewGeofence({
      name: '',
      type: 'port',
      portAreaType: 'Harbor',
      color: '#1E88C7'
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'port': return 'bg-primary/10 text-primary';
      case 'anchorage': return 'bg-success/10 text-success';
      case 'pilot': return 'bg-warning/10 text-warning';
      case 'terminal': return 'bg-info/10 text-info';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <DashboardLayout title="Geofence Management">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
        {/* Left - Geofence List */}
        <div className="flex flex-col gap-4">
          <Card className="flex-1 flex flex-col">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Pentagon className="w-5 h-5 text-primary" />
                  Daftar Geofence
                </CardTitle>
                <Button 
                  size="sm" 
                  className="gap-2"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <Plus className="w-4 h-4" />
                  Create Geofence
                </Button>
              </div>
              
              {/* Search */}
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cari geofence..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </CardHeader>

            <CardContent className="flex-1 p-0">
              <div className="overflow-auto max-h-[calc(100vh-340px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs">Nama</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Created</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredGeofences.map((geofence) => (
                      <TableRow 
                        key={geofence.id}
                        className={cn(
                          "transition-colors",
                          highlightedGeofence?.id === geofence.id && "bg-accent"
                        )}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: geofence.color }}
                            />
                            <span className="text-sm font-medium">{geofence.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={cn("text-xs", getTypeColor(geofence.type))}>
                            {geofence.portAreaType}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDate(geofence.createdAt)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleView(geofence)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleEdit(geofence)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleDelete(geofence)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            {['port', 'anchorage', 'pilot', 'terminal'].map((type) => {
              const count = geofences.filter(g => g.type === type).length;
              return (
                <Card key={type} className="p-3">
                  <p className="text-xs text-muted-foreground capitalize">{type}</p>
                  <p className="text-2xl font-semibold">{count}</p>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Right - Map */}
        <Card className="overflow-hidden">
          <div className="h-full min-h-[500px]">
            <VesselMap 
              showGeofences={true}
              highlightedGeofence={highlightedGeofence}
              onGeofenceClick={handleView}
            />
          </div>
        </Card>
      </div>

      {/* Create Geofence Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Geofence</DialogTitle>
            <DialogDescription>
              Define a new geofence area for vessel monitoring.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="Enter geofence name"
                value={newGeofence.name}
                onChange={(e) => setNewGeofence({ ...newGeofence, name: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={newGeofence.type}
                  onValueChange={(value) => setNewGeofence({ ...newGeofence, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="port">Port</SelectItem>
                    <SelectItem value="anchorage">Anchorage</SelectItem>
                    <SelectItem value="pilot">Pilot</SelectItem>
                    <SelectItem value="terminal">Terminal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Area Type</Label>
                <Select 
                  value={newGeofence.portAreaType}
                  onValueChange={(value) => setNewGeofence({ ...newGeofence, portAreaType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Harbor">Harbor</SelectItem>
                    <SelectItem value="Anchorage">Anchorage</SelectItem>
                    <SelectItem value="Pilot">Pilot Zone</SelectItem>
                    <SelectItem value="Container Terminal">Container Terminal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={newGeofence.color}
                  onChange={(e) => setNewGeofence({ ...newGeofence, color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={newGeofence.color}
                  onChange={(e) => setNewGeofence({ ...newGeofence, color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>

            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <MapPin className="w-3 h-3 inline mr-1" />
                Note: In a full implementation, you would draw the polygon on the map. 
                For this demo, a sample polygon will be created.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateGeofence} disabled={!newGeofence.name}>
              Create Geofence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Geofence</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedForDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Geofence Modal */}
      <GeofenceModal />
    </DashboardLayout>
  );
}
